/**
 * Who will read a comment, said before it is sent (RFC 0324 3c).
 *
 * The rule already exists: `routeComment` sends a root comment that names
 * nobody to the space's session, or starts one; `deliverMentions` wakes the
 * sessions a comment names; `answerComment` wakes the session that wrote the
 * comment being answered. This only reads that rule, from nothing but the
 * draft, so the popover, the column's reply box and an app all say the same.
 */

import {
  type CommentThread,
  commentListView,
  projectComments,
  projectSessions,
  replyWakes,
  threadPending,
} from '@mod/core/model'

export interface CommentReader {
  text: string
  /** `warn` when the comment would reach nobody who could answer it. */
  tone: 'normal' | 'warn'
}

export function commentReader(input: {
  projectDoc: unknown
  /** The thread's comments where they live (RFC 0339), with who read them.
   *  Absent, the project's index is read, which knows authors but not who
   *  read a thread and passed. */
  comments?: unknown
  /** What the draft names so far. Only sessions are woken by a mention. */
  mentions: ReadonlyArray<{ kind: string; id: string }>
  /** The comment being answered, for a reply. Absent for a new comment. */
  replyTo?: string
  /** The space's session, and whether a comment could start one. */
  space: { sessionId: string | null; canStart: boolean }
}): CommentReader {
  const titles = new Map(
    projectSessions(input.projectDoc).map((s) => [s.id, s.title]),
  )
  const name = (id: string) => titles.get(id) ?? id.slice(0, 8)

  const woken: string[] = []
  const add = (id: string | null) => {
    if (id && !woken.includes(id)) woken.push(id)
  }
  for (const m of input.mentions) if (m.kind === 'session') add(m.id)

  if (input.replyTo) {
    add(
      replyWakes(
        projectComments(input.comments ?? commentListView(input.projectDoc)),
        input.replyTo,
      ),
    )
    if (woken.length > 0)
      return { text: goesTo(woken.map(name)), tone: 'normal' }
    return {
      text: 'No session is woken. Mention one to ask it.',
      // Yellow: a reply that reaches nobody is the one case the line exists
      // to catch (3c), and the design keeps yellow for it.
      tone: 'warn',
    }
  }

  if (woken.length > 0) return { text: goesTo(woken.map(name)), tone: 'normal' }
  if (input.space.sessionId) {
    return {
      text: `Goes to ${name(input.space.sessionId)}, this space's session`,
      tone: 'normal',
    }
  }
  if (input.space.canStart) {
    return { text: 'Starts a session in this space', tone: 'normal' }
  }
  return {
    text: 'Nobody is paired here. Mention a session to ask one.',
    tone: 'warn',
  }
}

function goesTo(names: string[]): string {
  if (names.length === 1) return `Goes to ${names[0]}`
  const last = names[names.length - 1]
  return `Goes to ${names.slice(0, -1).join(', ')} and ${last}`
}

/** What a thread shows under its last comment while an agent has it (3d). */
export interface ThreadWaiting {
  state: 'reading' | 'working' | 'read'
  /** The session's name, for the line. */
  label: string
}

/**
 * What a thread is waiting for, from the project registry alone.
 *
 * The sessions the last comment could have woken, in the order to trust
 * them: the ones it named, the one a reply wakes, and for a root comment that
 * named nobody the space's session, which is where `routeComment` sent it.
 */
export function threadWaiting(input: {
  projectDoc: unknown
  /** As for `commentReader`: the comments where they live. */
  comments?: unknown
  thread: CommentThread
  spaceSessionId: string | null
  now?: number
}): ThreadWaiting | null {
  const { projectDoc, thread } = input
  const last = thread.replies[thread.replies.length - 1] ?? thread.root
  const candidates: string[] = []
  const add = (id: string | null | undefined) => {
    if (id && !candidates.includes(id)) candidates.push(id)
  }
  const named = (last.mentions ?? []).filter((m) => m.kind === 'session')
  for (const m of named) add(m.id)
  if (last.parentId) {
    add(
      replyWakes(
        projectComments(input.comments ?? commentListView(projectDoc)),
        last.parentId,
      ),
    )
  } else if (named.length === 0) {
    add(input.spaceSessionId)
  }
  const pending = threadPending(thread, projectDoc, candidates, input.now)
  if (!pending) return null
  const title = projectSessions(projectDoc).find(
    (s) => s.id === pending.sessionId,
  )?.title
  return {
    state: pending.state,
    label: title ?? pending.sessionId.slice(0, 8),
  }
}
